<template>
  <div id="pushConfigurationsList">
    <nav class="nav nav-tabs mb-3">
      <div class="nav-item"><router-link id="adminClustersLink" to="clusters" class="nav-link">{{ $t('a-pushConfigurations:clusters') | capitalize }}</router-link></div>
      <div class="nav-item"><router-link id="adminPushConfigurationsLink" to="push-configurations" class="nav-link">{{ $t('a-pushConfigurations:pushConfigurations') | capitalize }}</router-link></div>
    </nav>

    <card :title="$t('a-pushConfigurations:filter')" :visible="false" collapsible>
      <div slot="card-body">
        <div class="form-row">
          <div class="form-group col-md-4">
            <label for="filterName">{{ $t('a-pushConfigurations:name') | capitalize }}</label>
            <input id="filterName"
                   v-model="filterName"
                   v-validate="'text'"
                   :data-vv-as="$t('a-pushConfigurations:name')"
                   :class="{ 'is-invalid': errors.has('filterName') }"
                   type="text"
                   name="filterName"
                   class="form-control"
            >
            <div class="invalid-feedback">{{ errors.first('filterName') }}</div>
          </div>

          <div v-if="isRole.superAdmin" class="form-group col-md-4">
            <label for="filterCustomer">{{ $t('a-pushConfigurations:customer') | capitalize }}</label>
            <select id="filterCustomer" v-model="filterCustomer" name="filterCustomer" class="form-control">
              <option value="" />
              <option v-for="(c, indexCust) in $store.getters.customers" :key="indexCust" :value="c">{{ c.name }}</option>
            </select>
          </div>

          <div class="form-group col-md-4">
            <label for="filterType">{{ $t('a-pushConfigurations:type') | capitalize }}</label>
            <select id="filterType" v-model="filterType" name="filterType" class="form-control">
              <option value="" />
              <option value="HTTP">{{ $t('a-pushConfigurations:typeHttp') | capitalize }}</option>
              <option value="WEBSOCKET">{{ $t('a-pushConfigurations:typeWebsocket') | capitalize }}</option>
              <option value="MQTT">{{ $t('a-pushConfigurations:typeMqtt') | capitalize }}</option>
            </select>
          </div>
        </div>
      </div>

      <div slot="card-footer">
        <button id="filterButton" class="btn btn-primary" @click="filter()">{{ $t('a-pushConfigurations:search') | capitalize }}</button>
      </div>
    </card>

    <grid id="pushConfigurationsGrid"
          :title="$t('a-pushConfigurations:pushConfigurations')"
          :columns="columns"
          :paginated-dto="pushConfigurations"
          @load="filter"
    >
      <div slot="name" slot-scope="props" :data-push-configuration-id="props.dto.id" :data-push-configuration-name="props.dto.name" class="d-flex">
        <div class="mr-2"><i class="icon-pushConfiguration no-pointer" /></div>
        {{ props.dto.name }}
      </div>

      <div slot="actions" slot-scope="props" :data-push-configuration-id="props.dto.id" class="text-right">
        <i class="icon-preview" @click="displayDetailsModal(props.dto)" />
        <i :class="{ 'text-success': props.dto.health===true, 'text-danger': props.dto.health===false }" class="icon-health" @click="getPushConfigurationHealth(props.dto)" />
        <i :id="'editPushConfiguration-' + props.dto.id" class="icon-edit" @click="editPushConfiguration(props.dto)" />
        <i :id="'deletePushConfiguration-' + props.dto.id" class="icon-trash" @click="confirmDeletePushConfiguration(props.dto)" />
      </div>
    </grid>

    <modal id="detailsModal"
           v-model="showDetailsModal"
           :title="detailsModalTitle"
           size="lg"
    >
      <div slot="modal-body">
        <table class="table table-sm table-bordered">
          <tbody>
            <tr v-for="(value, attribute) in detailsModalBody" :key="attribute">
              <td v-if="value !== null">{{ $t('a-pushConfigurations:details.' + attribute) | capitalize }}</td>
              <td v-if="value !== null && attribute !== 'headers'">{{ value }}</td>
              <td v-if="value !== null && attribute === 'headers'">
                <div v-for="header in value" :key="header.key">{{ `${header.key}: ${header.value}` }}</div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </modal>

    <confirm id="deletePushConfiguration"
             v-model="showDeletePushConfigurationConfirm"
             :message="$t('a-pushConfigurations:confirmDelete', {configuration: pushConfigurationToDelete.name})"
             @confirm:validate="deletePushConfiguration"
    />

    <floating-button :click-handler="() => { showPushConfigurationForm=true }" icon-classes="icon-add" />
    <push-configurations-form v-model="showPushConfigurationForm" :push-configuration-to-edit="pushConfigurationToEdit" @success="filter" @cancel="pushConfigurationToEdit={}" />
  </div>
</template>

<script>
import { upperFirst, cloneDeep } from 'lodash-es'
import Vue from 'vue'
import to from '@/modules/await-to-js'
import eventBus from '@/modules/eventBus'

import PushConfigurationsForm from './PushConfigurationsForm'

export default {
  titleKey: 'administrationPushConfigurations',

  components: { PushConfigurationsForm },

  data () {
    return {
      // filters
      filterName: null,
      filterCustomer: null,
      filterType: null,
      // List
      pushConfigurations: {},
      showDeletePushConfigurationConfirm: false,
      pushConfigurationToDelete: {},
      pushConfigurationToEdit: {},
      // Display details in modal
      showDetailsModal: false,
      detailsModalTitle: '',
      detailsModalBody: {},
      // Add/edit
      showPushConfigurationForm: false
    }
  },

  computed: {
    columns () {
      const columns = [
        { text: this.$t('a-pushConfigurations:name'), slot: 'name', sort: 'name' }
      ]

      if (this.isRole.superAdmin) {
        columns.push({ text: this.$t('a-pushConfigurations:customer'), formatter: pushConfiguration => (pushConfiguration.customer ? pushConfiguration.customer.name : '') })
      }

      columns.push({ text: this.$t('a-pushConfigurations:type'), formatter: pushConfiguration => pushConfiguration.type })
      columns.push({ slot: 'actions' })

      return columns
    }
  },

  created () {
    this.filter()
    if (this.isRole.superAdmin) this.$store.dispatch('getCustomers')

    // Add active class to menu
    this.$nextTick(() => {
      const clusterMenu = document.querySelectorAll('a[href="#/administration/clusters"]')[0]
      if (clusterMenu) clusterMenu.classList.add('active')
    })
  },

  beforeDestroy () {
    // Remove active class to menu
    const clusterMenu = document.querySelectorAll('a[href="#/administration/clusters"]')[0]
    if (clusterMenu) clusterMenu.classList.remove('active')
  },

  methods: {
    async filter (options = {}) {
      const valid = await this.$validator.validateAll()
      if (!valid) return

      let search = []

      // Add criteria
      if (this.filterName) search = this.$utils.addSearch(search, 'like', '*' + this.filterName + '*', 'name')
      if (this.filterCustomer) search = this.$utils.addSearch(search, 'eq', this.filterCustomer.id, 'customer.id')
      if (this.filterType) search = this.$utils.addSearch(search, 'eq', this.filterType, 'type')

      // Format criteria
      if (search.length > 0) options.search = JSON.stringify(search.length === 1 ? search[0] : this.$utils.formatSearch('and', search))

      // Get filtered data
      this.getPushConfigurations(options)
    },

    async getPushConfigurations (options) {
      const params = this.$utils.getParams({ sort: '+name' }, options, this.queryPageParams)
      let error, response
      [error, response] = await to(this.$api.getPushConfigurations(this.$store.getters.application, params))
      if (error || !response) return

      this.pushConfigurations = response.data
    },

    async getPushConfigurationHealth (configuration) {
      let error, response
      [error, response] = await to(this.$api.getPushConfigurationHealth(configuration))
      if (error || !response) return

      // Color button for few seconds
      Vue.set(configuration, 'health', response.data.success)
      setTimeout(() => Vue.delete(configuration, 'health'), 1500)

      // Display result in a message
      if (response.data.success) eventBus.$emit('message-success', this.$t('a-pushConfigurations:healthSuccess', { configuration: configuration.name }))
      else eventBus.$emit('message-error', this.$t('a-pushConfigurations:healthError', { configuration: configuration.name, error: response.data.message }))
    },

    displayDetailsModal (dto) {
      this.detailsModalTitle = upperFirst(this.$t('a-pushConfigurations:detailsTitle', { configuration: dto.name }))
      this.detailsModalBody = cloneDeep(dto)
      // Remove name, links, customer, id
      delete this.detailsModalBody.id
      delete this.detailsModalBody.customer
      delete this.detailsModalBody.name
      delete this.detailsModalBody.links
      this.showDetailsModal = true
    },

    confirmDeletePushConfiguration (pushConfiguration) {
      this.showDeletePushConfigurationConfirm = true
      this.pushConfigurationToDelete = pushConfiguration
    },

    async deletePushConfiguration () {
      let error
      [error] = await to(this.$api.deletePushConfiguration(this.pushConfigurationToDelete))
      if (error) return

      // Success
      eventBus.$emit('message-success', this.$t('a-pushConfigurations:deleteSuccess', { configuration: this.pushConfigurationToDelete.name }))
      // Refresh list
      this.filter()
    },

    editPushConfiguration (pushConfiguration) {
      this.pushConfigurationToEdit = pushConfiguration
      this.showPushConfigurationForm = true
    }
  }
}
</script>

<style scoped lang="scss">
  .icon-check-circle {
    transition: all 0.2s;
  }
</style>
